/**
 * FeedbackFab — Floating 👍👎 feedback button
 *
 * Fixed to bottom-right of every authenticated page.
 * Collapsed: small FAB. Expanded: thumbs up/down + optional comment.
 * Auto-captures current route + timestamp + screenshot via html2canvas.
 * Gated behind VITE_FEEDBACK_MODE env var — renders nothing when off.
 */

import { useState, useCallback } from 'react';
import { useLocation } from 'react-router';
import {
  Fab,
  Box,
  Paper,
  IconButton,
  TextField,
  Typography,
  Fade,
  Stack,
  Tooltip,
} from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import FeedbackIcon from '@mui/icons-material/Feedback';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { useAuth } from '@/providers/AuthProvider';
import { supabase } from '@/lib/supabase';

/** Lazy-load html2canvas only when feedback mode is on */
let html2canvas: typeof import('html2canvas').default | null = null;
async function getHtml2Canvas() {
  if (!html2canvas) {
    const mod = await import('html2canvas');
    html2canvas = mod.default;
  }
  return html2canvas;
}

async function captureScreenshot(): Promise<string | null> {
  try {
    const h2c = await getHtml2Canvas();
    const canvas = await h2c(document.body, {
      logging: false,
      useCORS: true,
      allowTaint: true,
      scale: 2,              // Retina-quality for readable Chinese text + OCR
      // No maxWidth/maxHeight cap — downstream AI analysis needs full resolution
    });
    return canvas.toDataURL('image/jpeg', 0.85); // JPEG 85% quality — good text legibility
  } catch (err) {
    console.warn('[FeedbackFab] Screenshot capture failed:', err);
    return null;
  }
}

type Sentiment = 'thumbs_up' | 'thumbs_down';

export function FeedbackFab() {
  // Gate: do absolutely nothing when feedback mode is off
  if (import.meta.env.VITE_FEEDBACK_MODE !== 'true') return null;
  return <FeedbackFabInner />;
}

function FeedbackFabInner() {
  const [expanded, setExpanded] = useState(false);
  const [sentiment, setSentiment] = useState<Sentiment | null>(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  const handleFabClick = useCallback(() => {
    if (!expanded) {
      setExpanded(true);
      setSubmitted(false);
      setSentiment(null);
      setComment('');
    }
  }, [expanded]);

  const handleSentiment = useCallback((s: Sentiment) => {
    setSentiment(s);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!sentiment && !comment.trim()) return;
    setSubmitting(true);

    try {
      // Capture screenshot in parallel with getting session token
      const [screenshotData, { data: { session } }] = await Promise.all([
        captureScreenshot(),
        supabase.auth.getSession(),
      ]);

      const token = session?.access_token;
      if (!token) {
        console.error('[FeedbackFab] No auth token');
        setSubmitting(false);
        return;
      }

      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          source: 'fab',
          sentiment,
          tags: [],
          comment: comment.trim() || null,
          page_route: `${location.pathname}${location.search}`,
          screenshot_data: screenshotData,
        }),
      });

      if (!res.ok) {
        console.error('[FeedbackFab] Submit failed:', await res.text());
      } else {
        setSubmitted(true);
        // Auto-collapse after 1.5s
        setTimeout(() => {
          setExpanded(false);
          setSubmitted(false);
        }, 1500);
      }
    } catch (err) {
      console.error('[FeedbackFab] Error:', err);
    } finally {
      setSubmitting(false);
    }
  }, [sentiment, comment, location]);

  const handleClose = useCallback(() => {
    setExpanded(false);
    setSentiment(null);
    setComment('');
  }, []);

  // Only render for authenticated users. This gate must sit BELOW every hook:
  // `user` flips on login while the component is mounted, so returning early
  // above the useCallbacks changes the hook count between renders and React
  // throws "rendered more hooks than during the previous render".
  if (!user) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: (theme) => theme.zIndex.fab,
        // Stay below modals (zIndex.modal = 1300, fab = 1050)
      }}
    >
      {!expanded ? (
        /* Collapsed: just the FAB */
        <Tooltip title="提供反饋" placement="left">
          <Fab
            color="primary"
            size="medium"
            onClick={handleFabClick}
            sx={{
              boxShadow: 3,
              '&:hover': { transform: 'scale(1.1)' },
              transition: 'transform 0.2s',
            }}
          >
            <FeedbackIcon />
          </Fab>
        </Tooltip>
      ) : (
        /* Expanded panel */
        <Fade in={expanded}>
          <Paper
            elevation={6}
            sx={{
              p: 2,
              width: 280,
              borderRadius: 2,
            }}
          >
            {submitted ? (
              <Typography variant="body2" color="success.main" textAlign="center" py={1}>
                感謝你的反饋！ ✓
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" fontWeight={700}>
                    你的感受？
                  </Typography>
                  <IconButton size="small" onClick={handleClose}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>

                <Stack direction="row" spacing={1} justifyContent="center">
                  <IconButton
                    onClick={() => handleSentiment('thumbs_up')}
                    sx={{
                      border: 2,
                      borderColor: sentiment === 'thumbs_up' ? 'success.main' : 'divider',
                      borderRadius: '50%',
                      bgcolor: sentiment === 'thumbs_up' ? 'success.50' : 'transparent',
                      width: 56,
                      height: 56,
                    }}
                  >
                    <ThumbUpIcon
                      sx={{
                        fontSize: 28,
                        color: sentiment === 'thumbs_up' ? 'success.main' : 'grey.500',
                      }}
                    />
                  </IconButton>
                  <IconButton
                    onClick={() => handleSentiment('thumbs_down')}
                    sx={{
                      border: 2,
                      borderColor: sentiment === 'thumbs_down' ? 'error.main' : 'divider',
                      borderRadius: '50%',
                      bgcolor: sentiment === 'thumbs_down' ? 'error.50' : 'transparent',
                      width: 56,
                      height: 56,
                    }}
                  >
                    <ThumbDownIcon
                      sx={{
                        fontSize: 28,
                        color: sentiment === 'thumbs_down' ? 'error.main' : 'grey.500',
                      }}
                    />
                  </IconButton>
                </Stack>

                <TextField
                  placeholder="補充說明（選填）"
                  multiline
                  minRows={2}
                  maxRows={4}
                  fullWidth
                  size="small"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />

                <Stack direction="row" justifyContent="flex-end">
                  <Fab
                    size="small"
                    color="primary"
                    disabled={submitting || (!sentiment && !comment.trim())}
                    onClick={handleSubmit}
                  >
                    <SendIcon fontSize="small" />
                  </Fab>
                </Stack>
              </Stack>
            )}
          </Paper>
        </Fade>
      )}
    </Box>
  );
}
